// Importaciones necesarias
import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { AddBox as AddIcon } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { cancelButton, saveButton } from "../../../../core/styles/ui.constants";
import { handleFile } from "../../../../core/utils/file";
import { ADMINISTRACION, ADMINISTRADOR, DIRECTOR } from "../../../../core/utils/roles.constants";
import { useLoading } from "../../../../contexts/LoadingContext";
import { useAuth } from "../../../../contexts/AuthContext";
import IngresarItemsModal from "./IngresarItemsModal";
import FormularioEvaluacionProveedor from "./FormularioEvaluacionProveedor";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import ConfirmDialog from "../../../../components/Dialogs/ConfirmDialog";
import ABMProveedorModal from "./createProveedores";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "1200px",
  },
  dialogContentDialog: {
    padding: theme.spacing(4),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  titleText: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectorField: {
    width: "48%",
  },
  conceptoField: {
    width: "48%",
  },
  dateField: {
    width: "40%",
  },
  observationsField: {
    width: "58%",
  },
  providerField: {
    width: "48%",
  },
  stateField: {
    width: "48%",
  },
  buttonArea: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  itemText: {
    marginBottom: theme.spacing(1),
  },
  footerText: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(3),
  },
  footerButtons: {
    marginLeft: theme.spacing(2),
  },
  actionButton: saveButton,
  cancelButton: { ...cancelButton, marginLeft: theme.spacing(2) },
  eliminarAdjunto: { marginRight: theme.spacing(2) },
  areaBlanca: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
  },
}));

const CrearOrden = ({
  open,
  onClose,
  ordenDeCompra,
  tipoOrden,
  sectorParams,
}) => {
  const classes = useStyles();
  const { loading, show, hide } = useLoading();

  // Usuario local
  const { getUser } = useAuth();
  const user = getUser();

  const [editable, setEditable] = useState(false);

  const [openItemsModal, setOpenItemsModal] = useState(false);
  const [openFormEnd, setOpenFormEnd] = useState(false);
  const [existeAdjunto, setExisteAdjunto] = useState(false);

  const [openProveedor, setOpenProveedor] = useState(false);

  const [openConfirmDeleteComprobante, setOpenConfirmDeleteComprobante] = useState(false);
  const modalRef = useRef();

  const formDefaultValues = {
    sector: "",
    proveedor: [],
    concepto: "",
    fechaCreacion: new Date().toISOString().split("T")[0],
    observaciones: "",
    estado: "",
    tipoOrdenDeCompra: "",
    fechaAdjunto: "",
  };

  const [formData, setFormData] = useState(formDefaultValues);
  const [formDataOriginal, setFormDataOriginal] = useState("");
  const [errors, setErrors] = useState({});

  const [sectors, setSectors] = useState({});
  const [proveedores, setProveedores] = useState([]);
  const [estado, setEstado] = useState([]);
  const [items, setItems] = useState([]);
  const [evaluacionProveedor, setEvaluacionProveedor] = useState([]);

  const hasEvaluacion = evaluacionProveedor.length > 0;
  const estadoActual = String(formData.estado || "");
  const isLockedState = ["3"].includes(estadoActual);
  const canModify = editable && !isLockedState;

  const [sectorInicial, setSectorInicial] = useState(null);

  const [itemModalData, setItemModalData] = useState(null);
  const [itemModalIndex, setItemModalIndex] = useState(null);
  // const [itemModalReadOnly, setItemModalReadOnly] = useState(false);
  const [itemModalCanEnableEdit, setItemModalCanEnableEdit] = useState(false);

  /* -------------------- Ingresar fecha con el comprobante ------------------- */
  const fileInputRef = useRef(null);
  const [openFecha, setOpenFecha] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const refreshProveedores = async () => {
    const proveedorResponse = await api.proveedores.getAll();
    const proveedorMap = {};

    proveedorResponse
      .filter((p) => p.Rol === "proveedor")
      .forEach((proveedor) => {
        proveedorMap[proveedor.idProveedor] = proveedor.ApellidoyNombre;
      });

    setProveedores(proveedorMap);
    return proveedorMap;
  };

  const handleProveedorSuccess = async (nuevoProveedor) => {
    try {
      await refreshProveedores();

      const nuevoId = nuevoProveedor?.idProveedor?.toString();
      if (nuevoId) {
        setFormData((prev) => {
          const proveedoresActuales = Array.isArray(prev.proveedor)
            ? prev.proveedor
            : [];

          if (proveedoresActuales.includes(nuevoId)) {
            return prev;
          }

          if (prev.tipoOrdenDeCompra === "caja chica") {
            return { ...prev, proveedor: [...proveedoresActuales, nuevoId] };
          }

          return { ...prev, proveedor: [nuevoId] };
        });
      }

      setEditable(true);
    } catch (error) {
      console.error("Error al refrescar proveedores:", error.message);
    }
  };

  const handleNuevoClick = () => {
    if (sectorInicial !== null && sectors !== sectorInicial) {
      const confirmCambio = window.confirm(
        "El sector seleccionado es distinto al anterior. ¿Desea continuar?"
      );
      if (!confirmCambio) {
        setItems([]);
        return;
      }
    }

    // Si no hay sectorInicial, lo establecemos con el sector actual
    setSectorInicial(sectors);

    if (modalRef.current) {
      modalRef.current.resetFields();
    }
    setItemModalData(null);
    setItemModalIndex(null);
    // setItemModalReadOnly(false);
    setOpenItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setOpenItemsModal(false);
    setItemModalData(null);
    setItemModalIndex(null);
    // setItemModalReadOnly(false);
    setItemModalCanEnableEdit(false);
  };

  const handleVerDetalleItem = (item, index) => {
    const puedeEditarDetalle =
      [1, 2].includes(Number(estado));

    setItemModalData(item);
    setItemModalIndex(index);
    // setItemModalReadOnly(true);
    setItemModalCanEnableEdit(puedeEditarDetalle);
    setOpenItemsModal(true);
  }

  const handleDeleteItem = (index) => {
    if (!canModify) return;
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const handleSaveItem = (item) => {
    setItems((prevItems) => {
      if (itemModalIndex === null) {
        return [...prevItems, item];
      }

      return prevItems.map((prevItem, index) =>
        index === itemModalIndex ? item : prevItem
      );
    });
  };

  const handleClickUpload = () => {
    if (isLockedState) return;
    // Abrimos el modal para seleccionar la fecha
    setOpenFecha(true);
  };

  const deleteAdjunto = async () => {
    show("Eliminando adjunto...");
    try {
      await api.ordenesCompra.update(ordenDeCompra.IdOC, {
        Adjunto: "",
        Fecha_Adjunto: null,
      });

      setFormData({ ...formData, Adjunto: "", Fecha_Adjunto: "" });
      setExisteAdjunto(false);
      setOpenFecha(false);
      setSelectedDate(new Date().toISOString().split("T")[0]);
      setOpenConfirmDeleteComprobante(false);
    } catch (error) {
      console.error("Error al eliminar el adjunto:", error.message);
    } finally {
      hide();
    }
  };

  // Paso 1: al apretar el botón, abrir el input oculto
  const handleAdjuntoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Paso 2: al seleccionar el archivo, procesarlo
  const handleFileChange = async (e) => {
    show("Subiendo adjunto...");
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file) return;

    try {
      const result = await handleFile(file);

      // Aquí subimos el archivo y la fecha elegida
      await api.ordenesCompra.update(ordenDeCompra.IdOC, {
        Adjunto: result.path,
        Fecha_Adjunto: selectedDate,
      });

      ordenDeCompra.Adjunto = result.path;

      setExisteAdjunto(true);
    } catch (err) {
      console.error(err);
    } finally {
      if (input) input.value = "";
      setOpenFecha(false);
      setSelectedDate(new Date().toISOString().split("T")[0]);
      hide();
    }
  };

  const handleVerArchivo = (path) => {
    const backendUrl = process.env.REACT_APP_SERVER_URL;

    if (path.toLowerCase().endsWith(".pdf")) {
      window.open(`${backendUrl}${path}`, "_blank"); // abre PDF directamente
    } else {
      fetch(`${backendUrl}${path}`)
        .then((res) => res.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = path.split("/").pop();
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => console.error("Error al obtener archivo", err));
    }
  };

  const buildUpdatedOrden = (estadoOverride = null) => ({
    ...ordenDeCompra,
    IdSector: Number(formData.sector) || null,
    Concepto: formData.concepto || "",
    Fecha_Creacion: formData.fechaCreacion || null,
    Observaciones: formData.observaciones || "",
    IdEstado:
      estadoOverride !== null
        ? Number(estadoOverride)
        : Number(formData.estado) || ordenDeCompra.IdEstado,
    tipoOrdenDeCompra: formData.tipoOrdenDeCompra || null,
    Fecha_Adjunto: formData.fechaAdjunto || null,
    requisicion: {
      ...ordenDeCompra.requisicion,
      ItemsRequisicion: items.flatMap((item) =>
        item.informacionOS.map((info) => ({
          idRubro: Number(item.idRubro),
          descripcion: item.descripcion || "",
          idOS: info.selectedOs ? Number(info.selectedOs) : null,
          itemName: info.selectedItem || info.itemName || "",
          cantidad: String(info.cantidad || 0),
          importeUnitario: String(info.importeUnitario || 0),
        }))
      ),
    },
  });

  // Actualiza la orden con opción de forzar estado
  const actualizarOrden = async (estadoOverride = null) => {
    await api.ordenesCompra.update(ordenDeCompra.IdOC, {
      IdSector: Number(formData.sector) || null,
      Concepto: formData.concepto || "",
      Fecha_Creacion: formData.fechaCreacion || null,
      Observaciones: formData.observaciones || "",
      IdEstado:
        estadoOverride !== null
          ? Number(estadoOverride)
          : Number(formData.estado) || null,
      tipoOrdenDeCompra: formData.tipoOrdenDeCompra || null,
    });
  };

  // Actualiza relaciones OrdenCompra <-> Proveedores
  const actualizarRelacionesProveedores = async () => {
    if (!Array.isArray(formData.proveedor)) return;

    await api.ordenesDeCompraProveedor.delete(ordenDeCompra.IdOC);

    if (formData.proveedor.length > 0) {
      await Promise.all(
        formData.proveedor.map((idProveedor) =>
          api.ordenesDeCompraProveedor.create({
            IdOC: ordenDeCompra.IdOC,
            IdProveedor: Number(idProveedor),
          })
        )
      );
    }
  };

  // Actualiza los items de la requisición asociada a la orden
  const actualizarItems = async () => {
    const idRequisicion = ordenDeCompra.IdRequisicion;
    if (!idRequisicion) return;

    // Obtener items existentes y eliminarlos
    try {
      const itemsExistentes = await api.itemsRequisicionesXRequisicion(idRequisicion);
      if (Array.isArray(itemsExistentes) && itemsExistentes.length > 0) {
        await Promise.all(
          itemsExistentes.map((item) =>
            api.itemsRequisiciones.delete(item.idRequisionItem)
          )
        );
      }
    } catch (error) {
      // Si no hay items existentes (404), continuar
    }

    // Crear los nuevos items
    if (items.length > 0) {
      const itemsACrear = items.flatMap((item) =>
        item.informacionOS.map((info) => ({
          idRequision: idRequisicion,
          idRubro: Number(item.idRubro),
          descripcion: item.descripcion || "",
          idOS: info.selectedOs ? Number(info.selectedOs) : null,
          itemName: info.selectedItem || info.itemName || "",
          cantidad: String(info.cantidad || 0),
          importeUnitario: String(info.importeUnitario || 0),
        }))
      );

      await Promise.all(
        itemsACrear.map((item) => api.itemsRequisiciones.create(item))
      );
    }
  };

  // Flujo: 1→2 (enviar a aprobación), 2→3 (aprobar con evaluación)
  const handleOpenEnd = async (tipo) => {
    if (
      tipo !== "ver" &&
      (!formData.proveedor || formData.proveedor.length === 0)
    ) {
      setErrors({ ...errors, proveedor: "Debe seleccionar un proveedor" });
      return;
    }

    setErrors({});

    const estadoStr = String(formData.estado || "");

    // if (estadoStr === "2" && ![1, 5].includes(user.es_admin)) {
    if (estadoStr === "2") {
      return;
    }

    // Solo ver evaluaciones existentes
    if (tipo === "ver") {
      setOpenFormEnd(true);
      return;
    }

    // Estado 2: abrir evaluación antes de aprobar
    if (estadoStr === "2") {
      setOpenFormEnd(true);
      return;
    }

    // Estado 1: enviar a aprobación (actualiza a 2)
    if (estadoStr === "1") {
      show("Enviando a aprobación...");
      try {
        await actualizarOrden(2);
        await actualizarRelacionesProveedores();
        await actualizarItems();
        setFormData((prev) => ({ ...prev, estado: "2" }));
        onClose(buildUpdatedOrden(2));
      } catch (error) {
        console.error("Error al enviar a aprobación:", error.message);
      } finally {
        hide();
      }
      return;
    }

    // Otros estados: solo guardar
    show("Guardando orden...");
    try {
      await actualizarOrden();
      await actualizarRelacionesProveedores();
      await actualizarItems();
      onClose(buildUpdatedOrden());
    } catch (error) {
      console.error("Error al guardar la orden:", error.message);
    } finally {
      hide();
    }
  };

  // Reenviar a aprobación: cuando estado es 4 (rechazado), pasa a 2
  const handleReenviarAprobacion = async () => {
    show("Reenviando a aprobación...");
    try {
      await actualizarOrden(2);
      await actualizarRelacionesProveedores();
      await actualizarItems();
      setFormData((prev) => ({ ...prev, estado: "2" }));
      onClose(buildUpdatedOrden(2));
    } catch (error) {
      console.error("Error al reenviar a aprobación:", error.message);
    } finally {
      hide();
    }
  };

  // Rechazar: cuando estado es 2, pasa a 4 (rechazado)
  const handleReject = async () => {
    show("Rechazando orden...");
    try {
      await actualizarOrden(4);
      await actualizarRelacionesProveedores();
      setFormData((prev) => ({ ...prev, estado: "4" }));
      onClose(buildUpdatedOrden(4));
    } catch (error) {
      console.error("Error al rechazar la orden:", error.message);
    } finally {
      hide();
    }
  };

  const handleCloseEnd = () => setOpenFormEnd(false);

  const handleGuardarCambios = async () => {
    if (
      String(formData.estado || "") === "2" &&
      (!formData.proveedor || formData.proveedor.length === 0)
    ) {
      setErrors({ ...errors, proveedor: "Debe seleccionar un proveedor" });
      return;
    }

    show("Guardando cambios...");
    try {
      await actualizarOrden();
      await actualizarRelacionesProveedores();
      await actualizarItems();

      setFormDataOriginal({ ...formData });
      setErrors({});
      setEditable(false);
      onClose(buildUpdatedOrden());
    } catch (error) {
      console.error("Error al guardar cambios:", error.message);
    } finally {
      hide();
    }
  };

  const handleSuccessEvaluacion = async (nuevoEvaluacion) => {
    show("Guardando evaluaciones...");
    try {
      // --- Traer todas las evaluaciones existentes ---
      const evaluacionesExistentes = await api.evaluacionProveedor.getAll();

      // --- Filtrar solo las que corresponden a la orden y proveedores seleccionados ---
      const aEliminar = evaluacionesExistentes.filter(
        (item) => item.idOrdenCompra === ordenDeCompra.IdOC
      );

      // --- Eliminar en paralelo ---
      if (aEliminar.length > 0) {
        await Promise.all(
          aEliminar.map((item) =>
            api.evaluacionProveedor.delete(item.idevaluacion_proveedor_oc)
          )
        );
      }

      // --- Crear nuevas evaluaciones ---
      const evaluacionesValidas = nuevoEvaluacion.filter(
        (item) => item.Criterio_de_evaluacion && ordenDeCompra.IdOC
      );

      if (evaluacionesValidas.length > 0) {
        await Promise.all(
          evaluacionesValidas.map((item) =>
            api.evaluacionProveedor.create({
              idCriterioEvaluacion: parseInt(item.Criterio_de_evaluacion),
              idOrdenCompra: ordenDeCompra.IdOC,
              valor: item.valor,
            })
          )
        );
      }

      // --- Actualizar relaciones con los proveedores ---
      await actualizarRelacionesProveedores();
      await actualizarItems();

      // Aprobar: pasar estado a 3 una vez cargadas las evaluaciones
      await actualizarOrden(3);
      setFormData((prev) => ({ ...prev, estado: "3" }));

      handleCloseEnd();
      onClose(buildUpdatedOrden(3));
    } catch (error) {
      console.error("Error al guardar el formulario:", error.message);
    } finally {
      hide();
    }
  };

  useEffect(() => {
    if (open && ordenDeCompra) {
      const fetchData = async () => {
        const [
          // sectorResponse,
          proveedorResponse,
          estadoResponse,
          evaluacionProveedorResponse,
          ordenesProveedoresResponse,
        ] = await Promise.all([
          // api.sectores.getAll(),
          api.proveedores.getAll(),
          api.estados.getAll(),
          api.evaluacionProveedor.getAll(),
          api.ordenesDeCompraProveedor.getAll(),
        ]);

        const sectorMap = {};
        sectorParams.forEach((sector) => {
          sectorMap[sector.IdSector] = sector.Nombre;
        });

        const proveedorMap = {};
        proveedorResponse
          .filter((p) => p.Rol === "proveedor")
          .forEach((proveedor) => {
            proveedorMap[proveedor.idProveedor] = proveedor.ApellidoyNombre;
          });

        const estadoMap = {};
        estadoResponse.forEach((estado) => {
          estadoMap[estado.IdEstado] = estado.Detalle;
        });

        const evaluacionProveedorMap = evaluacionProveedorResponse.filter(
          (item) => item.idOrdenCompra === ordenDeCompra.IdOC
        );

        // obtenemos solo los proveedores de esta orden
        const proveedoresOrden = ordenesProveedoresResponse
          .filter((p) => p.IdOC === ordenDeCompra.IdOC)
          .map((p) => p.IdProveedor.toString()); // pasamos a string si el select lo necesita

        setProveedores(proveedorMap);
        setSectors(sectorMap);
        setEstado(estadoMap);
        setEvaluacionProveedor(evaluacionProveedorMap);

        // recién ahora seteo formData porque ya tengo los mapas cargados
        if (ordenDeCompra) {
          setFormData({
            sector: ordenDeCompra.IdSector?.toString() || "",
            proveedor: proveedoresOrden,
            concepto: ordenDeCompra.Concepto || "",
            fechaCreacion:
              ordenDeCompra.Fecha_Creacion ||
              new Date().toISOString().split("T")[0],
            observaciones: ordenDeCompra.Observaciones || "",
            estado: ordenDeCompra.IdEstado?.toString() || "",
            tipoOrdenDeCompra: ordenDeCompra.tipoOrdenDeCompra,
            fechaAdjunto: ordenDeCompra.Fecha_Adjunto || "",
          });

          setFormDataOriginal({
            sector: ordenDeCompra.IdSector?.toString() || "",
            proveedor: proveedoresOrden,
            concepto: ordenDeCompra.Concepto || "",
            fechaCreacion:
              ordenDeCompra.Fecha_Creacion ||
              new Date().toISOString().split("T")[0],
            observaciones: ordenDeCompra.Observaciones || "",
            estado: ordenDeCompra.IdEstado?.toString() || "",
            tipoOrdenDeCompra: ordenDeCompra.tipoOrdenDeCompra,
            fechaAdjunto: ordenDeCompra.Fecha_Adjunto || "",
          });

          if (ordenDeCompra.Adjunto) {
            setExisteAdjunto(true);
          }

          setItems(
            convertirItems(
              Array.isArray(ordenDeCompra.requisicion.ItemsRequisicion)
                ? ordenDeCompra.requisicion.ItemsRequisicion
                : []
            )
          );
        }
      };

      fetchData();
    } else {
      setErrors({});
      setProveedores([]);
      setEstado([]);
      setItems([]);
      setExisteAdjunto(false);
      setEditable(false);
    }
  }, [open]);

  const convertirItems = (itemsOS) => {
    if (!Array.isArray(itemsOS)) return [];

    const agrupadosPorRubro = {};

    itemsOS.forEach((item) => {
      const idRubro = item.idRubro;
      if (!agrupadosPorRubro[idRubro]) {
        agrupadosPorRubro[idRubro] = {
          idRubro: String(idRubro),
          nombreRubro: item.nombreRubro || "Varios",
          descripcion: item.descripcion || "",
          informacionOS: [],
        };
      }

      agrupadosPorRubro[idRubro].informacionOS.push({
        // idItemsOS: item.idItemOS,
        selectedOs: item.idOS,
        selectedItem: item.idItems,
        selectedItem: item.itemName,
        itemName: item.itemName,
        importeUnitario: item.importeUnitario,
        cantidad: String(item.cantidad),
        // cantidadMaxima: 6
      });
    });

    return Object.values(agrupadosPorRubro);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl">
        <DialogTitle disableTypography>
          <Typography variant="h6" className={classes.titleText}>
            {tipoOrden}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {ordenDeCompra && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {([ADMINISTRADOR, ADMINISTRACION].includes(user?.es_admin) ||
                (formData.estado === '1' && [DIRECTOR].includes(user?.es_admin))) && (
                  <Tooltip
                    title={
                      <span style={{ fontSize: "0.8rem" }}>
                        {editable ? "Cancelar edición" : "Habilitar edición"}
                      </span>
                    }
                  >
                    <IconButton
                      disabled={isLockedState}
                      onClick={() => {
                        if (editable) {
                          setFormData({
                            ...formDataOriginal,
                            // proveedor: formDataOriginal.proveedor,
                          });
                          setErrors({});
                        }
                        setEditable(!editable);
                      }}
                    >
                      {editable ? <CloseIcon /> : <EditIcon />}
                    </IconButton>
                  </Tooltip>
                )}
            </div>
          )}
          {/* Primera fila */}
          <div className={classes.formRow}>
            {/* Sector */}
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1">Sector</Typography>
              <Select
                variant="outlined"
                fullWidth
                disabled={true}
                value={formData.sector || ""}
                error={!!errors.sector || undefined}
                onChange={(e) =>
                  setFormData({ ...formData, sector: e.target.value })
                }
              >
                {Object.keys(sectors).map((key) => (
                  <MenuItem key={key} value={key.toString()}>
                    {sectors[key]}
                  </MenuItem>
                ))}
              </Select>
              {errors.sector && (
                <Typography color="error" variant="body2">
                  {errors.sector}
                </Typography>
              )}
            </div>
            {/* Concepto */}
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1">Concepto</Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={formData.concepto}
                error={!!errors.concepto || undefined}
                onChange={(e) =>
                  setFormData({ ...formData, concepto: e.target.value })
                }
                disabled={!canModify}
              />
              {errors.concepto && (
                <Typography color="error" variant="body2">
                  {errors.concepto}
                </Typography>
              )}
            </div>
            {/* Fecha de creación */}
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1">Fecha de Creación</Typography>
              <TextField
                variant="outlined"
                type="date"
                fullWidth
                value={formData.fechaCreacion}
                error={!!errors.fechaCreacion || undefined}
                onChange={(e) =>
                  setFormData({ ...formData, fechaCreacion: e.target.value })
                }
                disabled={!canModify}
              />
              {errors.fechaCreacion && (
                <Typography color="error" variant="body2">
                  {errors.fechaCreacion}
                </Typography>
              )}
            </div>
          </div>

          {/* Fila de Proveedor y Estado */}
          <div className={classes.formRow}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">Proveedor *</Typography>
                <ABMProveedorModal
                  open={openProveedor}
                  onClose={() => setOpenProveedor(false)}
                  onSuccess={handleProveedorSuccess}
                  tipoCliente="proveedor"
                />
                {editable && (
                  <Tooltip
                    title={
                      <span style={{ fontSize: "0.8rem" }}>Agregar proveedor</span>
                    }
                    arrow
                  >
                    <IconButton
                      color="primary"
                      onClick={() => setOpenProveedor(true)}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <Select
                variant="outlined"
                fullWidth
                multiple={formData.tipoOrdenDeCompra === "caja chica"}
                value={
                  Array.isArray(formData.proveedor) ? formData.proveedor : []
                }
                error={!!errors.proveedor}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    proveedor: Array.isArray(value) ? value : [value],
                  });
                  if (errors.proveedor) {
                    setErrors((prev) => {
                      const { proveedor, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                disabled={!canModify}
                renderValue={(selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={proveedores[value]}
                        size="small"
                      />
                    ))}
                  </div>
                )}
              >
                {Object.keys(proveedores).map((key) => (
                  <MenuItem key={key} value={key.toString()}>
                    {proveedores[key]}
                  </MenuItem>
                ))}
              </Select>

              {errors.proveedor && (
                <Typography color="error" variant="body2">
                  {errors.proveedor}
                </Typography>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1">Estado</Typography>
              <Select
                variant="outlined"
                fullWidth
                value={formData.estado || ""}
                error={!!errors.estado || undefined}
                disabled={true}
              >
                {Object.keys(estado).map((key) => (
                  <MenuItem key={key} value={key.toString()}>
                    {estado[key]}
                  </MenuItem>
                ))}
              </Select>
            </div>
            {formData.fechaAdjunto && (
              <div style={{ flex: 1 }}>
                {/* Fecha del adjunto */}
                <Typography variant="subtitle1">Fecha del adjunto</Typography>
                <TextField
                  variant="outlined"
                  type="date"
                  fullWidth
                  value={formData.fechaAdjunto ?? ""}
                  error={!!errors.fechaAdjunto || undefined}
                  onChange={(e) =>
                    setFormData({ ...formData, fechaAdjunto: e.target.value })
                  }
                  disabled={!canModify}
                />
              </div>
            )}
          </div>

          {/* Área blanca con botones Adjuntar y Nuevo */}
          <div className={classes.areaBlanca}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" className={classes.itemText}>
                Items
              </Typography>
              {/* {!hasEvaluacion && (
                <div>
                  <BotonGenerico
                    tipo={"nuevo"}
                    color="primary"
                    onClick={handleNuevoItemsModal}
                    text="Nuevo"
                    disabled={!canModify}
                  />
                </div>
              )} */}
              {(canModify && user.es_admin === ADMINISTRACION) && (
                <BotonGenerico
                  tipo="nuevo"
                  color="primary"
                  onClick={handleNuevoClick}
                  text="Nuevo"
                  disabled={loading}
                />
              )}
              {/* {(!formData.sector && canModify) ? (
                <Tooltip
                  title={
                    <span style={{ fontSize: "0.8rem" }}>
                      Se debe seleccionar un sector
                    </span>
                  }
                  classes={{ tooltip: classes.customTooltip }}
                  arrow
                >
                  <span>
                    <BotonGenerico
                      tipo="nuevo"
                      color="primary"
                      onClick={handleNuevoClick}
                      text="Nuevo"
                      disabled
                    />
                  </span>
                </Tooltip>
              ) : (
                <BotonGenerico
                  tipo="nuevo"
                  color="primary"
                  onClick={handleNuevoClick}
                  text="Nuevo"
                  disabled={loading}
                />
              )} */}
            </div>

            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Rubro</TableCell>
                    <TableCell align="center">Orden de servicio</TableCell>
                    {(canModify && user.es_admin === ADMINISTRACION) && (
                      <TableCell align="center">Opciones</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {item.nombreRubro || ""}
                      </TableCell>
                      <TableCell align="center">
                        {item.informacionOS.map((info, idx) => (
                          <div key={idx}>
                            OS: {info.selectedOs} — Item: {info.selectedItem} —
                            Cantidad: {info.cantidad || 0} — Saldo unitario: $
                            {info.importeUnitario || 0} — Total: $
                            {(info.importeUnitario || 0) * (info.cantidad || 0)}
                          </div>
                        ))}
                      </TableCell>
                      {(canModify && user.es_admin === ADMINISTRACION) && (
                        <TableCell align="center">
                          <IconButton onClick={() => handleVerDetalleItem(item, index)}>
                            <SearchIcon color="primary" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteItem(index)}
                            disabled={loading}
                          >
                            <DeleteIcon color="secondary" />
                          </IconButton>
                        </TableCell>
                        // <TableCell align="center">
                        //   <IconButton
                        //     aria-label="Eliminar ítem"
                        //     onClick={() => handleDeleteItem(index)}
                        //   >
                        //     <DeleteIcon color="secondary" />
                        //   </IconButton>
                        // </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <FormularioEvaluacionProveedor
            open={openFormEnd}
            onClose={handleCloseEnd}
            evaluacionProveedor={evaluacionProveedor}
            onSuccess={handleSuccessEvaluacion}
            editable={editable || evaluacionProveedor.length === 0}
          />
          {openItemsModal && (
            <IngresarItemsModal
              ref={modalRef}
              open={openItemsModal}
              onClose={handleCloseItemsModal}
              onSabeItem={handleSaveItem}
              sectorSelected={formData.sector}
              // limpiarItems={() => setItems([])}
              initialItem={itemModalData}
              // readOnly={itemModalReadOnly}
              canEnableEdit={itemModalCanEnableEdit}
            />
          )}
        </DialogContent>
        <DialogFooter>
          {(() => {
            const estadoStr = String(formData.estado || "");
            const buttonText =
              // estadoStr === "2" && [1, 5].includes(user.es_admin)
              estadoStr === "2"
                ? "Recepcionar orden"
                // : estadoStr === "1" && [1, 2].includes(user.es_admin)
                : estadoStr === "1"
                  ? "Confirmar orden"
                  : evaluacionProveedor.length > 0
                    ? "Ver evaluaciones"
                    : "Guardar";
            const onClickHandler = () =>
              handleOpenEnd(evaluacionProveedor.length > 0 ? "ver" : "guardar");
            return (
              <>
                {!existeAdjunto ? (
                  <>
                    {/* { */}
                    {[1, 5].includes(user.es_admin) ||
                      (formData.estado === 1 && [DIRECTOR].includes(user.es_admin)) ||
                      (formData.estado === 2 && [ADMINISTRACION].includes(user.es_admin)) ? (
                      // (formData.estado === 1 || formData.estado === 2) ? (
                      <BotonGenerico
                        tipo={"adjuntar"}
                        color="primary"
                        // onClick={handleAdjuntoClick}
                        onClick={handleClickUpload}
                        text="Adjuntar comprobante"
                        disabled={isLockedState}
                      />) : (<></>)}
                  </>
                ) : (
                  <>
                    <BotonGenerico
                      tipo={"adjuntar"}
                      color="primary"
                      onClick={() => handleVerArchivo(ordenDeCompra.Adjunto)}
                      text="Ver comprobante"
                    />
                    {/* {!isLockedState && editable && [1, 5].includes(user.es_admin) && ( */}
                    {!isLockedState && editable && (
                      <BotonGenerico
                        tipo={"rechazar"}
                        color="secondary"
                        classesParams={classes.eliminarAdjunto}
                        onClick={() => setOpenConfirmDeleteComprobante(true)}
                        text="Eliminar comprobante"
                      />
                    )}
                  </>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept=".pdf"
                />
                {(canModify && user.es_admin == ADMINISTRACION) && (
                  <BotonGenerico
                    tipo="guardar"
                    color="primary"
                    onClick={handleGuardarCambios}
                    text="Guardar cambios"
                  />
                )}
                {(!isLockedState || hasEvaluacion) &&
                  estadoStr !== "4" &&
                  // (estadoStr !== "1" || [1, 2].includes(user.es_admin)) &&
                  // (estadoStr !== "2" || [1, 5].includes(user.es_admin)) && (
                  estadoStr !== "1" && estadoStr !== "2" && (
                    <div className={canModify ? classes.footerButtons : undefined}>
                      <BotonGenerico
                        tipo="guardar"
                        color="primary"
                        onClick={onClickHandler}
                        text={buttonText}
                      />
                    </div>
                  )}

                {(
                  // ["1", "2"].includes(estadoStr) ||
                  (estadoStr === "1" && [DIRECTOR].includes(user.es_admin)) ||
                  (estadoStr === "2" && [ADMINISTRACION].includes(user.es_admin))
                  // ["1", "2"].includes(estadoStr) || estadoStr === "2" || estadoStr === "1"
                ) && (
                    <BotonGenerico
                      tipo={"rechazar"}
                      color="secondary"
                      onClick={handleReject}
                      text="Rechazar"
                    />
                  )}

                {/* {estadoStr === "4" && [1, 2].includes(user.es_admin) && ( */}
                {estadoStr === "4" && (
                  <div style={{ marginLeft: 16 }}>
                    <BotonGenerico
                      tipo="guardar"
                      color="primary"
                      onClick={handleReenviarAprobacion}
                      text="Reenviar a aprobación"
                    />
                  </div>
                )}

                {/* <BotonGenerico
                  color="secondary"
                  onClick={onClose}
                  text={!evaluacionProveedor.length > 0 ? "Cancelar" : "Cerrar"}
                /> */}
              </>
            );
          })()}
        </DialogFooter>
        <ConfirmDialog
          open={openConfirmDeleteComprobante}
          onClose={() => setOpenConfirmDeleteComprobante(false)}
          onConfirm={deleteAdjunto}
          title="Eliminar comprobante"
          description="¿Estás seguro de que querés eliminar este comprobante?"
          confirmText="Eliminar"
          cancelText="Cancelar"
        />
      </Dialog>

      <Dialog open={openFecha} onClose={() => setOpenFecha(false)}>
        <DialogTitle>Seleccionar fecha del archivo</DialogTitle>
        <DialogContent className={classes.dialogContentDialog}>
          <TextField
            variant="outlined"
            type="date"
            fullWidth
            value={selectedDate || ""}
            onChange={(e) => setSelectedDate(e)}
          />
        </DialogContent>
        <DialogFooter>
          <BotonGenerico
            tipo="guardar"
            color="primary"
            onClick={handleAdjuntoClick}
            text="Guardar"
          />
          <BotonGenerico
            tipo="cancelar"
            color="secondary"
            onClick={() => setOpenFecha(false)}
            text="Cancelar"
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default CrearOrden;
